import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { FaIcon, faCoins, faBedFrontSolid, faFileInvoice, faMoneyBillTrendUp, faCalculator } from '@/lib/fa'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

interface BusinessTripEditSummaryProps {
  tripData: any
  companyId: string
}

interface SummaryItem {
  name: string
  amount: number
  negative?: boolean
  icon?: IconDefinition | string // FontAwesome icon or SVG path
}

const SummaryColumns: TableHeaderColumn[] = [
  { title: 'POZYCJA', key: 'name', type: 'string', sort: false },
  { title: 'KWOTA', key: 'amount', type: 'number', sort: false },
]

const BusinessTripEditSummary: React.FC<BusinessTripEditSummaryProps> = ({
  tripData,
  companyId,
}) => {
  const { t } = useLocalize()

  const summaryItems = React.useMemo<SummaryItem[]>(() => {
    const items: SummaryItem[] = []

    // Allowances total
    const allowancesTotal = (tripData?.allowances || []).reduce(
      (total: number, current: any) =>
        total + (current.amount || 0) * (current.exchangeRate || 1),
      0,
    )
    items.push({
      name: t('newDelegation.summary.allowances') || 'Diety',
      amount: allowancesTotal,
      icon: faCoins,
    })

    // Accommodations total
    const accommodationsTotal = (tripData?.accommodations || []).reduce(
      (total: number, current: any) => total + (current.plnAmount || 0),
      0,
    )
    items.push({
      name: t('newDelegation.summary.accomodation') || 'Noclegi',
      amount: accommodationsTotal,
      icon: faBedFrontSolid,
    })

    // Invoices total
    const invoicesTotal = (tripData?.invoices || []).reduce(
      (total: number, current: any) => total + (current.plnAmount || 0),
      0,
    )
    items.push({
      name: t('newDelegation.summary.invoices') || 'Koszty',
      amount: invoicesTotal,
      icon: faFileInvoice,
    })

    // Advance Payments total (negative)
    const advancePaymentsTotal = (tripData?.advancePayments || []).reduce(
      (total: number, current: any) => total + (current.plnAmount || 0),
      0,
    )
    items.push({
      name: t('newDelegation.summary.advance-payments') || 'Zaliczki',
      amount: advancePaymentsTotal,
      negative: true,
      icon: faMoneyBillTrendUp,
    })

    // Total sum
    const totalSum = items.reduce(
      (total: number, current: SummaryItem) =>
        total + (current.negative ? -current.amount : current.amount),
      0,
    )
    items.push({
      name: t('newDelegation.summary.sum') || 'Razem',
      amount: totalSum,
      icon: faCalculator,
    })

    return items
  }, [tripData, t])

  const formatNumber = (value: number, isNegative?: boolean): string => {
    if (!value && value !== 0) return '-'
    const num = Math.abs(value)
    const formatted = num % 1 === 0 ? num.toString() : parseFloat(num.toFixed(2)).toString()
    return `${isNegative ? '-' : ''}${formatted} PLN`
  }

  return (
    <div className="">
      <div className="tw-rounded-lg tw-bg-white tw-pb-4">
        {/* Action Bar */}
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
          {/* Empty action bar for consistency */}
          <div className="tw-flex tw-items-center tw-gap-4"></div>
        </div>

        {/* Summary table */}
        <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-base-300">
          <table className="tw-w-full tw-table-auto tw-border-collapse">
            <TableHeader
              columns={SummaryColumns}
              fontSize="md"
              isSelectedAny={false}
              rounded
              sortConfig={null}
              bolder
              disabledToggle={true}
              toggleAll={() => {}}
              onHeaderClick={() => {}}
            />
            <tbody>
              {summaryItems.map((item, index) => {
                const isLast = index === summaryItems.length - 1
                const isAdvancePayment = item.negative
                return (
                  <tr
                    key={index}
                    className={`hover:tw-bg-base-50 ${
                      isLast ? 'tw-bg-base-100' : ''
                    }`}
                  >
                    <TableTextRow>
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {item.icon && (
                          typeof item.icon === 'string' ? (
                            <img
                              src={item.icon}
                              alt=""
                              className="tw-h-5 tw-w-5"
                              onError={(e) => {
                                // Hide icon if it fails to load
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : (
                            <FaIcon
                              icon={item.icon}
                              className="tw-h-5 tw-w-5"
                            />
                          )
                        )}
                        <span
                          className={isLast ? 'tw-font-semibold' : ''}
                          style={isAdvancePayment ? { color: '#F00' } : {}}
                        >
                          {item.name}
                        </span>
                      </div>
                    </TableTextRow>
                    <TableTextRow>
                      <span
                        className={isLast ? 'tw-font-semibold' : ''}
                        style={isAdvancePayment ? { color: '#F00' } : {}}
                      >
                        {formatNumber(item.amount, item.negative)}
                      </span>
                    </TableTextRow>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BusinessTripEditSummary

